import React, { createContext, useContext, useState, useEffect } from 'react';

const WishlistContext = createContext();

export const WishlistProvider = ({ children }) => {
  const [wishlistItems, setWishlistItems] = useState([]);
  const [currentUserId, setCurrentUserId] = useState(null);

  // Load current user and their wishlist
  useEffect(() => {
    const currentUser = JSON.parse(localStorage.getItem('currentUser') || 'null');
    if (currentUser?.id) {
      setCurrentUserId(currentUser.id);
      const userWishlist = localStorage.getItem(`jrweb_user_${currentUser.id}_wishlist`);
      if (userWishlist) {
        setWishlistItems(JSON.parse(userWishlist));
      }
    } else {
      // For non-logged-in users, use general wishlist
      const generalWishlist = localStorage.getItem('jrweb_wishlist');
      if (generalWishlist) {
        setWishlistItems(JSON.parse(generalWishlist));
      }
    }
  }, []);

  // Save wishlist to localStorage whenever it changes
  useEffect(() => {
    if (currentUserId) {
      localStorage.setItem(`jrweb_user_${currentUserId}_wishlist`, JSON.stringify(wishlistItems));
    } else {
      localStorage.setItem('jrweb_wishlist', JSON.stringify(wishlistItems));
    }
  }, [wishlistItems, currentUserId]);

  // Update user ID when user logs in/out
  useEffect(() => {
    const handleStorageChange = () => {
      const currentUser = JSON.parse(localStorage.getItem('currentUser') || 'null');
      if (currentUser?.id !== currentUserId) {
        if (currentUser?.id) {
          setCurrentUserId(currentUser.id);
          const userWishlist = localStorage.getItem(`jrweb_user_${currentUser.id}_wishlist`);
          if (userWishlist) {
            setWishlistItems(JSON.parse(userWishlist));
          } else {
            setWishlistItems([]);
          }
        } else {
          setCurrentUserId(null);
          setWishlistItems([]);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [currentUserId]);

  const addToWishlist = (product) => {
    if (!isInWishlist(product.id)) {
      setWishlistItems(prev => [...prev, product]);
    }
  };

  const removeFromWishlist = (productId) => {
    setWishlistItems(prev => prev.filter(item => item.id !== productId));
  };

  const isInWishlist = (productId) => {
    return wishlistItems.some(item => item.id === productId);
  };

  const clearWishlist = () => {
    setWishlistItems([]);
  };

  return (
    <WishlistContext.Provider value={{
      wishlistItems,
      addToWishlist,
      removeFromWishlist,
      isInWishlist,
      clearWishlist
    }}>
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
};